/**
 * Security utilities for data validation and protection
 * Implements GDPR compliance and data minimization principles
 */

// Data validation utilities
export const validateSessionId = (sessionId: string): boolean => {
  // UUID-like format validation with timestamp
  const sessionPattern = /^[a-f0-9]{4}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-\d{13}$/i
  return sessionPattern.test(sessionId) && sessionId.length >= 20 && sessionId.length <= 50
}

export const validateEventType = (eventType: string): boolean => {
  const validEvents = [
    'session_start',
    'session_end', 
    'audio_play_start',
    'audio_play_pause',
    'audio_milestone_3min',
    'audio_milestone_5min',
    'survey_displayed',
    'survey_completed',
    'survey_skipped',
    'completion_reached',
    'error_occurred',
    'performance_metric'
  ]
  return validEvents.includes(eventType)
}

export const validateDeviceType = (deviceType: string): boolean => {
  return ['desktop', 'tablet', 'mobile'].includes(deviceType)
}

export const validateErrorType = (errorType: string): boolean => {
  return ['network', 'device', 'hls', 'session', 'timeout', 'general', 'performance'].includes(errorType)
}

export const validateSurveyRating = (rating: string): boolean => {
  return ['excellent', 'good', 'fair', 'poor'].includes(rating)
}

// Data sanitization utilities
export const sanitizeUserAgent = (userAgent: string): string => {
  // Truncate and remove potentially sensitive information
  return userAgent
    .substring(0, 200)
    .replace(/\b(?:email|phone|ssn|password|token|key|secret)\b/gi, '[REDACTED]')
}

export const sanitizeErrorMessage = (errorMessage: string): string => {
  // Remove sensitive patterns and truncate
  return errorMessage
    .substring(0, 500)
    .replace(/\b(?:password|token|key|secret|api_key|bearer|auth)\s*[:=]\s*\S+/gi, '[REDACTED]')
    .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[EMAIL]')
    .replace(/\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g, '[CARD]')
}

export const sanitizeReferrer = (referrer: string): string => {
  try {
    const url = new URL(referrer)
    // Only keep domain and basic path, remove query parameters and fragments
    return `${url.protocol}//${url.hostname}${url.pathname.split('/').slice(0, 3).join('/')}`
  } catch {
    return 'unknown'
  }
}

// GDPR compliance utilities
export const minimizeAnalyticsData = (data: any): any => {
  const minimized = { ...data }
  
  // Remove or truncate potentially sensitive fields
  if (minimized.userAgent) {
    minimized.userAgent = sanitizeUserAgent(minimized.userAgent)
  }
  
  if (minimized.errorMessage) {
    minimized.errorMessage = sanitizeErrorMessage(minimized.errorMessage)
  }
  
  if (minimized.referrer) {
    minimized.referrer = sanitizeReferrer(minimized.referrer)
  }
  
  // Remove any accidentally included sensitive fields
  const sensitiveFields = ['email', 'phone', 'name', 'address', 'ip', 'userId', 'username']
  sensitiveFields.forEach(field => {
    if (field in minimized) {
      delete minimized[field]
    }
  })
  
  return minimized
}

// Rate limiting utilities
class RateLimiter {
  private requests: Map<string, number[]> = new Map()
  private readonly maxRequests: number
  private readonly timeWindow: number // in milliseconds
  
  constructor(maxRequests: number = 100, timeWindowMinutes: number = 1) {
    this.maxRequests = maxRequests
    this.timeWindow = timeWindowMinutes * 60 * 1000
  }
  
  isAllowed(identifier: string): boolean {
    const now = Date.now()
    const requests = this.requests.get(identifier) || []
    
    // Remove old requests outside the time window
    const validRequests = requests.filter(time => now - time < this.timeWindow)
    
    if (validRequests.length >= this.maxRequests) {
      return false
    }
    
    // Add current request
    validRequests.push(now)
    this.requests.set(identifier, validRequests)
    
    return true
  }
  
  getRemainingRequests(identifier: string): number {
    const now = Date.now()
    const requests = this.requests.get(identifier) || []
    const validRequests = requests.filter(time => now - time < this.timeWindow)
    
    return Math.max(0, this.maxRequests - validRequests.length)
  }
  
  getResetTime(identifier: string): Date {
    const now = Date.now()
    const requests = this.requests.get(identifier) || []
    const oldestValidRequest = requests.find(time => now - time < this.timeWindow)
    
    return new Date((oldestValidRequest || now) + this.timeWindow)
  }
}

// Export rate limiter instances
export const analyticsRateLimiter = new RateLimiter(50, 1) // 50 events per minute per session
export const errorRateLimiter = new RateLimiter(10, 1) // 10 errors per minute per session

// Content Security Policy utilities
export const generateCSPNonce = (): string => {
  const array = new Uint8Array(16)
  crypto.getRandomValues(array)
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
}

// Session security utilities
export const isSecureContext = (): boolean => {
  return window.isSecureContext || location.protocol === 'https:' || location.hostname === 'localhost'
}

export const validateSecurityHeaders = (): { [key: string]: boolean } => {
  const checks = {
    https: isSecureContext(),
    csp: document.querySelector('meta[http-equiv="Content-Security-Policy"]') !== null,
    xFrameOptions: true, // Would need server-side validation
    xContentTypeOptions: true, // Would need server-side validation
    referrerPolicy: true // Would need server-side validation
  }
  
  return checks
}

// Input validation utilities
export const validateNumericRange = (value: number, min: number, max: number): boolean => {
  return typeof value === 'number' && !isNaN(value) && value >= min && value <= max
}

export const validateStringLength = (value: string, maxLength: number): boolean => {
  return typeof value === 'string' && value.length <= maxLength
}

export const validateTimestamp = (timestamp: any): boolean => {
  if (timestamp instanceof Date) {
    return !isNaN(timestamp.getTime())
  }
  
  if (typeof timestamp === 'number') {
    return !isNaN(timestamp) && timestamp > 0
  }
  
  // Firebase Timestamp object
  if (timestamp && typeof timestamp.toDate === 'function') {
    return true
  }
  
  return false
}

// Audit logging utilities
export const createAuditLog = (action: string, details: any): void => {
  const auditEntry = {
    action,
    timestamp: new Date().toISOString(),
    sessionId: generateSessionId(),
    details: minimizeAnalyticsData(details),
    userAgent: navigator.userAgent.substring(0, 100),
    location: {
      pathname: location.pathname,
      search: location.search.substring(0, 100) // Truncate to avoid sensitive data
    }
  }
  
  // In development, log to console
  if (process.env.NODE_ENV === 'development') {
    console.log('Audit Log:', auditEntry)
  }
  
  // In production, this would be sent to a secure audit logging service
  // For now, store in sessionStorage for debugging
  try {
    const auditLogs = JSON.parse(sessionStorage.getItem('auditLogs') || '[]')
    auditLogs.push(auditEntry)
    
    // Keep only last 50 audit logs
    if (auditLogs.length > 50) {
      auditLogs.splice(0, auditLogs.length - 50)
    }
    
    sessionStorage.setItem('auditLogs', JSON.stringify(auditLogs))
  } catch (error) {
    console.warn('Failed to store audit log:', error)
  }
}

// Generate secure session ID
function generateSessionId(): string {
  // Generate anonymous session ID (UUID v4 format with timestamp)
  return 'xxxx-xxxx-4xxx-yxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  }) + '-' + Date.now()
}

// Export data validation schema
export const analyticsEventSchema = {
  required: ['eventType', 'sessionId', 'timestamp'],
  properties: {
    eventType: { validator: validateEventType },
    sessionId: { validator: validateSessionId },
    deviceType: { validator: validateDeviceType, optional: true },
    errorType: { validator: validateErrorType, optional: true },
    surveyRating: { validator: validateSurveyRating, optional: true },
    playbackDuration: { validator: (v: number) => validateNumericRange(v, 0, 86400), optional: true },
    audioPosition: { validator: (v: number) => validateNumericRange(v, 0, 86400), optional: true },
    ttfp: { validator: (v: number) => validateNumericRange(v, 0, 60000), optional: true },
    errorMessage: { validator: (v: string) => validateStringLength(v, 500), optional: true },
    userAgent: { validator: (v: string) => validateStringLength(v, 200), optional: true }
  }
}

export const validateAnalyticsEvent = (data: any): { valid: boolean; errors: string[] } => {
  const errors: string[] = []
  
  // Check required fields
  for (const field of analyticsEventSchema.required) {
    if (!(field in data)) {
      errors.push(`Missing required field: ${field}`)
    }
  }
  
  // Validate each property
  for (const [field, config] of Object.entries(analyticsEventSchema.properties)) {
    if (field in data) {
      if (!(config as any).validator((data as any)[field])) {
        errors.push(`Invalid value for field: ${field}`)
      }
    } else if (!(config as any).optional) {
      errors.push(`Missing required field: ${field}`)
    }
  }
  
  return {
    valid: errors.length === 0,
    errors
  }
}